import { computed, ref, watch } from 'vue'
import { hoursService } from '@/services/hoursService.js'

/**
 * Composable for EmployeeHoursBarChart data fetching and computation.
 * Handles weekly availability, detailed hours, capacity, billability,
 * and stacked series building.
 *
 * @param {import('vue').Ref|import('vue').ComputedRef} props - Component props (reactive)
 * @param {Function} getColorFor - Color resolver function (label => hex color)
 * @param {import('vue').Ref<boolean>} isMobile - Whether the viewport is mobile-sized
 */
export function useBarChartData(props, getColorFor, isMobile) {
  const toHours = val => Number(val || 0)
  const toDays = val => Number(val || 0) / 8

  // --- Weekly availability data ---
  const customWeeklyData = ref([])
  const isLoadingWeeklyData = ref(false)

  const cleanDate = dateStr => {
    if (!dateStr) return null
    return String(dateStr).substring(0, 10)
  }

  const fetchWeeklyAvailability = async () => {
    const startDate = cleanDate(props.periodStart)
    const endDate = cleanDate(props.periodEnd)

    if (!props.employee?.simplicate_id || !startDate || !endDate) {
      customWeeklyData.value = []
      return
    }

    isLoadingWeeklyData.value = true
    try {
      const result = await hoursService.getWeeklyAvailability({
        start_date: startDate,
        end_date: endDate
      })

      const empData = result.data?.find(e => e.simplicate_id === props.employee.simplicate_id)

      if (!empData) {
        console.warn('[BarChart] Employee not found in API response:', {
          employeeId: props.employee.simplicate_id,
          period: `${startDate} - ${endDate}`,
          responseCount: result.data?.length || 0
        })
      } else if (!empData.weeks || empData.weeks.length === 0) {
        console.warn('[BarChart] Employee found but no weeks data:', {
          employeeId: props.employee.simplicate_id,
          period: `${startDate} - ${endDate}`
        })
      }

      customWeeklyData.value = empData?.weeks || []
    } catch (err) {
      console.error('Error fetching weekly availability:', err)
      customWeeklyData.value = []
    } finally {
      isLoadingWeeklyData.value = false
    }
  }

  watch(
    () => [props.employee?.simplicate_id, props.periodStart, props.periodEnd],
    fetchWeeklyAvailability,
    { immediate: true }
  )

  const activeWeeks = computed(() => {
    if (customWeeklyData.value.length > 0) {
      return customWeeklyData.value
    }
    return props.employee?.weeks || []
  })

  const weekRanges = computed(() => {
    return activeWeeks.value.map(w => ({
      start: w.week_start,
      end: w.week_end,
      iso_week: w.iso_week,
      booked_hours: w.booked_hours,
      assignment_hours: w.assignment_hours,
      proposed_hours: w.proposed_hours || 0,
      proposed_assignments: w.proposed_assignments || [],
      el_hours: w.el_hours || 0,
      weekly_capacity_days: w.weekly_capacity_days
    }))
  })

  const currentWeekIndex = computed(() => {
    const today = new Date().toISOString().split('T')[0]
    return weekRanges.value.findIndex(w => today >= w.start && today <= w.end)
  })

  const categories = computed(() => {
    return weekRanges.value.map(w => w.iso_week || '')
  })

  // --- Billability ---
  const billabilityPerWeek = computed(() => {
    if (!props.showBillability || !props.contractHoursPerWeek || props.contractHoursPerWeek <= 0) {
      return []
    }

    return activeWeeks.value.map(week => {
      const billableHours = Number(week.billable_hours || 0)
      const percentage = Math.round((billableHours / props.contractHoursPerWeek) * 100)
      return percentage
    })
  })

  // --- Capacity ---
  const capacityValue = computed(() => {
    const contractual = Number(props.employee?.contractual_hours_per_week || 0)
    const fte = Number(props.employee?.fte_ratio || 1)
    const maxBillability = Number(props.employee?.max_billability || 100) / 100
    const hours = contractual > 0 ? contractual : 40 * (isFinite(fte) ? fte : 1)
    const billableHours = hours * maxBillability
    return props.unit === 'days' ? billableHours / 8 : billableHours
  })

  const weeklyCapacity = computed(() => {
    return weekRanges.value.map(w => {
      if (w.weekly_capacity_days !== undefined && w.weekly_capacity_days !== null) {
        return props.unit === 'days' ? w.weekly_capacity_days : w.weekly_capacity_days * 8
      }
      return capacityValue.value
    })
  })

  // --- Base data series ---
  const dataSeriesBase = computed(() => {
    const mapVal = props.unit === 'days' ? toDays : toHours
    const extra = props.extraProposedHoursByWeek || {}
    // When set, subtract this preference's proposed hours from the proposed
    // series — used on Concretiseren where the same role is already shown as
    // the grey potential bar (prevents double counting for the PL-selected
    // employee).
    const excludeId =
      props.excludeProposedPreferenceId != null ? Number(props.excludeProposedPreferenceId) : null
    return {
      submittedTotal: weekRanges.value.map(w => mapVal(w.booked_hours)),
      assigned: weekRanges.value.map(w => mapVal(w.assignment_hours)),
      proposed: weekRanges.value.map(w => {
        let proposedHours = Number(w.proposed_hours || 0)
        if (excludeId != null) {
          const excluded = (w.proposed_assignments || [])
            .filter(d => Number(d.preference_id) === excludeId)
            .reduce((sum, d) => sum + Number(d.hours_this_week || 0), 0)
          proposedHours = Math.max(0, proposedHours - excluded)
        }
        return mapVal(proposedHours + (extra[w.start] || 0))
      })
    }
  })

  // --- Detailed hours ---
  const hourRows = ref([])
  const isLoadingHours = ref(false)

  const fetchDetailed = async () => {
    if (!props.employee?.simplicate_id || !props.periodStart || !props.periodEnd) {
      hourRows.value = []
      isLoadingHours.value = false
      return
    }
    isLoadingHours.value = true
    try {
      const rows = await hoursService.getHours({
        employee_id: props.employee.simplicate_id,
        start_date: props.periodStart,
        end_date: props.periodEnd,
        limit: 5000
      })
      hourRows.value = Array.isArray(rows) ? rows : []
    } catch (e) {
      console.error('Failed to load detailed hours:', e)
      hourRows.value = []
    } finally {
      isLoadingHours.value = false
    }
  }

  watch(() => [props.employee?.simplicate_id, props.periodStart, props.periodEnd], fetchDetailed, {
    immediate: true
  })

  // --- Chart ready state ---
  const chartReady = computed(() => {
    const hasAnyWeeks = activeWeeks.value.length > 0 || props.employee?.weeks?.length > 0
    if (!hasAnyWeeks) return false
    if (isLoadingHours.value) return false
    return true
  })

  // --- Stacked submitted series ---
  const submittedSeries = computed(() => {
    const toUnit = props.unit === 'days' ? toDays : toHours
    const weekCount = weekRanges.value.length
    const byLabel = new Map()

    for (const row of hourRows.value) {
      const lbl = row.type_label || 'Overig'
      if (!byLabel.has(lbl)) byLabel.set(lbl, Array(weekCount).fill(0))
    }

    for (const row of hourRows.value) {
      const dateStr = String(row.start_date).split('T')[0]
      const lbl = row.type_label || 'Overig'
      const buckets = byLabel.get(lbl)
      for (let i = 0; i < weekRanges.value.length; i++) {
        const { start, end } = weekRanges.value[i]
        if (dateStr >= start && dateStr <= end) {
          buckets[i] += Number(row.hours || 0)
          break
        }
      }
    }

    const labelsWithTotals = [...byLabel.entries()]
      .map(([lbl, arr]) => ({ lbl, total: arr.reduce((s, v) => s + v, 0), data: arr }))
      .filter(item => item.total > 0)

    const pmIndex = labelsWithTotals.findIndex(item =>
      item.lbl.toLowerCase().includes('projectmanagement')
    )
    const pmItem = pmIndex >= 0 ? labelsWithTotals.splice(pmIndex, 1)[0] : null

    labelsWithTotals.sort((a, b) => b.total - a.total)

    const orderedLabels = pmItem ? [pmItem, ...labelsWithTotals] : labelsWithTotals

    const numWeeks = weekRanges.value.length
    const barWidth = isMobile.value
      ? numWeeks > 16
        ? 12
        : numWeeks > 10
          ? 18
          : numWeeks > 6
            ? 25
            : 40
      : undefined

    return orderedLabels.map(item => ({
      name: item.lbl,
      type: 'bar',
      stack: 'work',
      barMaxWidth: barWidth,
      data: item.data.map(v => toUnit(v)),
      itemStyle: { color: getColorFor(item.lbl) }
    }))
  })

  // --- Potential assignment ---
  const potentialAssignmentData = computed(() => {
    if (!props.potentialDpw || props.potentialDpw <= 0) return []
    const weekCount = weekRanges.value.length
    const hoursPerWeek = props.potentialDpw * 8
    const value = props.unit === 'days' ? props.potentialDpw : hoursPerWeek
    return Array(weekCount).fill(value)
  })

  // --- Projects for tooltip ---
  const getProjectsForWeek = weekIndex => {
    const weekData = weekRanges.value[weekIndex]
    if (!weekData) return []

    const projectMap = new Map()
    const toUnit = props.unit === 'days' ? toDays : toHours

    for (const row of hourRows.value) {
      const dateStr = String(row.start_date).split('T')[0]
      if (dateStr >= weekData.start && dateStr <= weekData.end) {
        const typeLabel = (row.type_label || '').toLowerCase()
        if (typeLabel !== 'projectmanagement') continue
        if (!row.project_name) continue

        const projectName = row.project_name
        const existing = projectMap.get(projectName) || 0
        projectMap.set(projectName, existing + Number(row.hours || 0))
      }
    }

    return [...projectMap.entries()]
      .map(([name, hours]) => ({ name, hours: toUnit(hours) }))
      .sort((a, b) => b.hours - a.hours)
  }

  return {
    // State
    isLoadingWeeklyData,
    isLoadingHours,
    customWeeklyData,
    hourRows,
    // Computed
    activeWeeks,
    weekRanges,
    currentWeekIndex,
    categories,
    billabilityPerWeek,
    capacityValue,
    weeklyCapacity,
    dataSeriesBase,
    chartReady,
    submittedSeries,
    potentialAssignmentData,
    // Methods
    getProjectsForWeek
  }
}
